import React, { useEffect, useCallback } from "react";

interface UseIdleTimeoutProps {
  timeout: number; // Tiempo de inactividad en milisegundos (30 minutos en este caso)
  onLogout: () => void; // Función de logout
  isLogin: boolean; // Estado de login
}

const UseIdleTimeout = ({
  timeout,
  onLogout,
  isLogin,
}: UseIdleTimeoutProps) => {
  const timeoutId = React.useRef<number | null>(null);
  const STORAGE_KEY = "pestaña_actividad_global";

  const resetTimeout = useCallback(
    (isFromStorage = false) => {
      if (timeoutId.current !== null) window.clearTimeout(timeoutId.current);

      // Si la actividad es local (mouse, click), informamos a las otras pestañas
      if (!isFromStorage && isLogin) {
        localStorage.setItem(STORAGE_KEY, Date.now().toString());
      }

      timeoutId.current = window.setTimeout(() => {
        // ANTES de desloguear, verificamos si realmente pasó el tiempo
        // (por si otra pestaña actualizó el storage hace un segundo)
        const lastActivity = Number(localStorage.getItem(STORAGE_KEY) || 0);
        const now = Date.now();

        if (now - lastActivity >= timeout) {
          onLogout();
        } else {
          // Si otra pestaña estuvo activa, reprogramamos la diferencia
          resetTimeout(true);
        }
      }, timeout);
    },
    [timeout, onLogout, isLogin]
  );

  useEffect(() => {
    if (isLogin) {
      const events = [
        "mousemove",
        "mousedown",
        "keypress",
        "scroll",
        "touchstart",
      ];

      const handleActivity = () => resetTimeout(false);

      // ESCUCHAR otras pestañas
      const handleStorage = (e: StorageEvent) => {
        if (e.key === STORAGE_KEY) resetTimeout(true);
      };

      events.forEach((e) => window.addEventListener(e, handleActivity));
      window.addEventListener("storage", handleStorage);

      resetTimeout();

      return () => {
        if (timeoutId.current !== null) window.clearTimeout(timeoutId.current);
        events.forEach((e) => window.removeEventListener(e, handleActivity));
        window.removeEventListener("storage", handleStorage);
      };
    }
  }, [isLogin, resetTimeout]);

  return null;
};

export default UseIdleTimeout;
